import { create } from 'zustand';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../lib/firebase';
import { setSentryUser } from '../lib/sentry';

export type Role =
  | 'ADMIN'
  | 'STAFF'
  | 'SALES'
  | 'SALES_MANAGER'
  | 'MEASURER'
  | 'FINANCE'
  | 'FINANCE_MANAGER'
  | 'PURCHASER'
  | null;

/**
 * Default route a user should land on after sign-in.
 *
 *   MEASURER         → /staff/schedule     (their only operational surface)
 *   FINANCE          → /staff/payments     (the reconciliation list is their day)
 *   FINANCE_MANAGER  → /staff/claims/finance (the claims approval queue —
 *                                          their primary surface)
 *   PURCHASER        → /staff/requisitions (the PR list is their primary surface)
 *   STAFF (baseline) → /staff/receipts     (after ADR 0005 Revision 1 — staff
 *                                          submits receipts; finance assembles
 *                                          claims from the queue)
 *   ADMIN / SALES    → /staff/home         (overview dashboard)
 *
 * Never `/staff` — the calculator rehydrates the last quote, a documented
 * magnet for editing the wrong one. See ADR 0005.
 */
export const defaultLandingForRole = (role: Role): string => {
  if (role === 'MEASURER') return '/staff/schedule';
  if (role === 'FINANCE') return '/staff/payments';
  if (role === 'FINANCE_MANAGER') return '/staff/claims/finance';
  if (role === 'PURCHASER') return '/staff/requisitions';
  if (role === 'STAFF') return '/staff/receipts';
  if (role === 'SALES_MANAGER') return '/staff/quotes';
  return '/staff/home';
};

/**
 * Routes a measurer is allowed to reach. Anything else redirects to their
 * default landing.
 */
const MEASURER_ALLOWED_PREFIXES = [
  '/staff/schedule',
  // Measurement detail and opening editor live under /staff/orders/:id/...
  // We allow that prefix only for the measurement subtree (checked at runtime).
  '/staff/onboard',
  // Every signed-in role can submit + view their own receipts (ADR 0005 Rev 1).
  '/staff/receipts',
];

/**
 * Routes a finance user is allowed to reach. Finance is read-mostly with two
 * write powers (record manual payments, advance PR status). They DON'T see
 * the calculator (no quote creation), the measurement schedule, deliveries,
 * vendor master edits, or admin settings.
 */
const FINANCE_ALLOWED_PREFIXES = [
  '/staff/payments',
  // Same page as /staff/payments, opened on the Invoices tab (the Operations
  // nav entry). Without the prefix the fence bounces finance off their own
  // register.
  '/staff/invoices',
  '/staff/work-order-register',
  '/staff/quotes',
  '/staff/orders',
  '/staff/customers',
  '/staff/vendors',
  '/staff/requisitions',
  '/staff/purchase-orders',
  '/staff/claims',
  '/staff/cash-float',
  // Phase A: finance can submit their own receipts; Phase B adds the inbox
  // at /staff/receipts/inbox (same prefix).
  '/staff/receipts',
  '/staff/home',
  '/staff/onboard',
];

/**
 * Routes a purchaser is allowed to reach. Procurement-focused: requisitions
 * (read + manual create), vendor master data, and read-only context across
 * quotes / orders / customers (so the purchaser can link a manual PR to
 * a quote or order, and look up a customer for context). They DON'T see
 * the calculator, schedule, deliveries, payments, or admin.
 */
const PURCHASER_ALLOWED_PREFIXES = [
  '/staff/requisitions',
  '/staff/purchase-orders',
  '/staff/work-orders',
  '/staff/vendors',
  '/staff/orders',
  '/staff/customers',
  '/staff/quotes',
  '/staff/claims',
  '/staff/cash-float',
  '/staff/receipts',
  '/staff/home',
  '/staff/onboard',
];

/**
 * Routes a finance manager is allowed to reach. Same reconciliation surface
 * as FINANCE plus the claims approval queue once Phase 1 ships. Read-mostly:
 * the actual approve + push-to-payout write actions are gated server-side
 * inside the claims Cloud Functions, not by route.
 */
const FINANCE_MANAGER_ALLOWED_PREFIXES = [
  '/staff/payments',
  '/staff/invoices',
  '/staff/work-order-register',
  '/staff/claims',
  '/staff/cash-float',
  '/staff/quotes',
  '/staff/orders',
  '/staff/customers',
  '/staff/vendors',
  '/staff/requisitions',
  '/staff/purchase-orders',
  '/staff/receipts',
  '/staff/home',
  '/staff/onboard',
];

/**
 * Routes a baseline STAFF user is allowed to reach. ADR 0005 Revision 1
 * flipped the primary STAFF surface from `/staff/claims` to `/staff/receipts`
 * (receipt-first inversion). `/staff/claims` stays on the list so STAFF can
 * click through from a receipt's "Attached to CLM-…" badge to a read-only
 * claim view in Phase B; without `/staff/claims` here, that click-through
 * would bounce them back to `/staff/receipts`.
 */
const STAFF_ALLOWED_PREFIXES = [
  '/staff/home',
  '/staff/receipts',
  '/staff/claims',
  '/staff/onboard',
];

/**
 * Routes a SALES_MANAGER (pipeline oversight) is allowed to reach: the sales
 * pipeline + the read-across operational surfaces, scoped cross-rep. They do
 * NOT see the calculator (`/staff` — quote authoring is a rep concern), the
 * measurement schedule, deliveries, finance payments, procurement, or admin.
 */
const SALES_MANAGER_ALLOWED_PREFIXES = [
  '/staff/quotes',
  '/staff/orders',
  // Cross-rep read on the Invoices register, matching their cross-rep read on
  // quotes and orders. NOT '/staff/payments' — the finance entry point stays
  // finance's.
  '/staff/invoices',
  '/staff/reports',
  '/staff/customers',
  '/staff/home',
  '/staff/receipts',
  '/staff/onboard',
];

export const isPathAllowedForRole = (role: Role, pathname: string): boolean => {
  if (role === 'MEASURER') {
    if (MEASURER_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p))) return true;
    // Permit measurement detail / opening edit URLs:
    // /staff/orders/:projectId/measurements/...
    if (/^\/staff\/orders\/[^/]+\/measurements(\/|$)/.test(pathname)) return true;
    return false;
  }
  if (role === 'FINANCE') {
    return FINANCE_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  if (role === 'FINANCE_MANAGER') {
    return FINANCE_MANAGER_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  if (role === 'PURCHASER') {
    return PURCHASER_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  if (role === 'STAFF') {
    return STAFF_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  if (role === 'SALES_MANAGER') {
    return SALES_MANAGER_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
  }
  return true;
};

export interface StaffProfile {
  fullName?: string;
  whatsapp?: string;
}

/**
 * Minimal shape of a `users/{email}` Firestore document for the fields the
 * auth listener reads. Typed locally (not a full entity) — only what's used
 * here. `extraAccess` is validated with `Array.isArray` before use, so it's
 * intentionally `unknown`.
 */
interface UserDocData {
  role?: Role;
  fullName?: string;
  whatsapp?: string;
  extraAccess?: unknown;
}

interface AuthState {
  user: User | null;
  role: Role;
  profile: StaffProfile | null;
  /**
   * Route-path prefixes granted to this user on top of their role default.
   * See ADR 0005 §3 — admin maintains these via the Staff Manager.
   * `ProtectedRoute` admits when role matches OR `pathname.startsWith` any
   * prefix in this array.
   */
  extraAccess: string[];
  authInitialized: boolean;
  setUser: (
    user: User | null,
    role: Role,
    profile?: StaffProfile | null,
    extraAccess?: string[],
  ) => void;
  logout: () => Promise<void>;
}

import { useQuoteStore } from './useQuoteStore';

// sessionStorage marker that distinguishes "fresh app session" (new tab, after
// a tab close, or after a real sign-out) from "mid-session refresh" (F5 in the
// middle of editing). sessionStorage clears on tab close, so its presence
// proves we're inside the same tab/session the user is still working in.
//
// Used to decide whether to reset the persisted quote workspace at sign-in:
//   - marker missing OR mismatched email → fresh session → wipe the workspace
//     so the calculator opens clean (matches user expectation; the magnet for
//     "edited the wrong quote" was always the auto-rehydrate of stale state)
//   - marker matches → same tab, same user → keep the workspace so a mid-edit
//     refresh doesn't lose unsaved cart work
const SESSION_MARKER_KEY = 'wss-active-session-email';

const isSameSession = (email: string | null): boolean => {
  if (typeof sessionStorage === 'undefined') return true;
  return sessionStorage.getItem(SESSION_MARKER_KEY) === email;
};

const stampSession = (email: string | null): void => {
  if (typeof sessionStorage === 'undefined') return;
  if (email) sessionStorage.setItem(SESSION_MARKER_KEY, email);
  else sessionStorage.removeItem(SESSION_MARKER_KEY);
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  role: null,
  profile: null,
  extraAccess: [],
  authInitialized: false,
  setUser: (user, role, profile = null, extraAccess = []) => {
    // Detect "this is a fresh sign-in" — either no marker yet (new tab) or
    // a different user is signing in (logout-then-login as someone else,
    // or an emulator dev-account switch). Either case warrants a clean
    // workspace.
    // The same-session case (page refresh while signed in) skips the reset
    // so unsaved cart work survives accidental F5.
    const incomingEmail = user?.email?.toLowerCase() ?? null;
    if (!isSameSession(incomingEmail)) {
      useQuoteStore.getState().resetWorkspace();
      stampSession(incomingEmail);
    }
    set({ user, role, profile, extraAccess, authInitialized: true });
    // Non-PII identity on Sentry events: uid + role, never the email.
    setSentryUser(user?.uid ?? null, role);
  },
  logout: async () => {
    await signOut(auth);
    set({ user: null, role: null, profile: null, extraAccess: [], authInitialized: true });
    setSentryUser(null, null);
    // Belt-and-braces: clear the workspace and session marker on explicit
    // sign-out so the next sign-in starts fresh even if sessionStorage
    // somehow survives the tab.
    useQuoteStore.getState().resetWorkspace();
    stampSession(null);
  },
}));

/**
 * Mirror the user's Firestore role onto their auth token as a `role` custom
 * claim. storage.rules gates on `request.auth.token.role` because the Storage
 * rules engine can't reach Firestore via cross-service firestore.get() — this
 * is what populates that claim. The cached token is checked first, so the
 * refreshRoleClaim callable and the forced token refresh only run when the
 * claim is missing or stale (first sign-in, or after an admin changes a role).
 * Best-effort: a failure is logged and never blocks sign-in.
 */
const ensureRoleClaim = async (user: User, role: Role): Promise<void> => {
  try {
    const { claims } = await user.getIdTokenResult();
    if (claims.role === role) return;
    await httpsCallable(functions, 'refreshRoleClaim')();
    await user.getIdToken(true);
  } catch (err) {
    console.warn('Role claim sync failed (non-blocking).', err);
  }
};

// Primary Session Verification & Security Guard
onAuthStateChanged(auth, (user) => {
  void (async () => {
    if (user && user.email) {
      const email = user.email.toLowerCase();

      try {
        // Zero-Day Bootstrapper to prevent irreversible Admin lockout
        if (email === 'ben@wss.com.sg') {
          const adminRef = doc(db, 'users', email);
          const adminSnap = await getDoc(adminRef);
          if (!adminSnap.exists()) {
            await setDoc(adminRef, { role: 'ADMIN', invitedAt: new Date().toISOString() });
            // Audit the bootstrap event. This is a privilege-grant action; if
            // ben@wss.com.sg is ever taken over, the audit trail proves when
            // the upgrade happened and from where. Best-effort — never block
            // sign-in on a failed audit write.
            try {
              const auditRef = doc(db, 'bootstrapEvents', `${Date.now()}-${user.uid}`);
              await setDoc(auditRef, {
                email,
                uid: user.uid,
                at: new Date().toISOString(),
                userAgent:
                  typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 256) : '',
              });
            } catch (auditErr) {
              console.warn('Bootstrap audit log failed (non-blocking)', auditErr);
            }
            console.warn(
              '[SECURITY] Admin bootstrap self-provision occurred for',
              email,
              'at',
              new Date().toISOString(),
            );
            await ensureRoleClaim(user, 'ADMIN');
            useAuthStore.getState().setUser(user, 'ADMIN', null, []);
          } else {
            const data = adminSnap.data() as UserDocData;
            await ensureRoleClaim(user, 'ADMIN');
            useAuthStore
              .getState()
              .setUser(
                user,
                'ADMIN',
                { fullName: data.fullName, whatsapp: data.whatsapp },
                Array.isArray(data.extraAccess) ? (data.extraAccess as string[]) : [],
              );
          }
          return;
        }

        // Active Database Validation Handshake
        const userRef = doc(db, 'users', email);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          console.warn(
            'Authentication violently severed: Domain Identity not found in central registry.',
          );
          await signOut(auth);
          useAuthStore.getState().setUser(null, null, null);
          return;
        }

        const data = userSnap.data() as UserDocData;
        const role = data.role as Role;
        await ensureRoleClaim(user, role);
        useAuthStore
          .getState()
          .setUser(
            user,
            role,
            { fullName: data.fullName, whatsapp: data.whatsapp },
            Array.isArray(data.extraAccess) ? (data.extraAccess as string[]) : [],
          );
      } catch (error) {
        console.error('Critical connection failure mapping security clearance matrix: ', error);
        await signOut(auth);
        useAuthStore.getState().setUser(null, null, null, []);
      }
    } else {
      useAuthStore.getState().setUser(null, null, null, []);
    }
  })();
});
